import { useDialog } from 'contexts/dialog';
import { useNavigationGuard } from 'contexts/navigationGuard';
import useBeforeUnload from 'hooks/useBeforeUnload';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

const useAssignmentEditorNavigation = ({ isDirty, isSaving = false, backPath }) => {
    const history = useHistory();
    const { setDialog, closeDialog } = useDialog();
    const { onBeforeNavigate } = useNavigationGuard();
    const { t } = useTranslation('assignment');

    const showUnsavedChangesDialog = useCallback(
        (navigate) => {
            setDialog({
                open: true,
                message: t('common:dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
                description: t(
                    'common:dialog.unsaved-changes.description',
                    'If you exit without saving, all unsaved changes will be lost.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.exit', 'Exit'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    closeDialog();
                    navigate();
                },
                onCancel: closeDialog,
                onClose: closeDialog,
            });
        },
        [closeDialog, setDialog, t]
    );

    const onClickBack = useCallback(() => {
        if (!isDirty) {
            history.push(backPath);
            return;
        }
        showUnsavedChangesDialog(() => history.push(backPath));
    }, [backPath, history, isDirty, showUnsavedChangesDialog]);

    useEffect(() => {
        onBeforeNavigate((navigate) => {
            if (!isDirty) return navigate();
            showUnsavedChangesDialog(navigate);
        });

        return () => onBeforeNavigate(null);
    }, [isDirty, onBeforeNavigate, showUnsavedChangesDialog]);

    useBeforeUnload(isDirty || isSaving);

    return onClickBack;
};

export default useAssignmentEditorNavigation;
